import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PlayButton, PauseButton } from '../../assets/images';

type props = {
  url: string;
  handleAudioPlay?: (data: any) => void;
  isTimeDisplay?: boolean;
  audioDuration?: number | string | null;
};

const parseDuration = (input?: number | string | null) => {
  if (input === null || input === undefined) return undefined;
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input <= 0) return undefined;
    // Heuristic: if value is extremely large (likely milliseconds), convert.
    if (input > 24 * 60 * 60) {
      const millisToSeconds = input / 1000;
      return millisToSeconds > 0 ? millisToSeconds : undefined;
    }
    return input;
  }
  if (typeof input === 'string') {
    const parts = input.split(':').map((part) => Number(part));
    if (parts.some((part) => Number.isNaN(part))) return undefined;
    while (parts.length < 3) {
      parts.unshift(0);
    }
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }
  return undefined;
};

const AudioPlayer: React.FunctionComponent<props> = ({ url, isTimeDisplay, audioDuration, handleAudioPlay }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isPaused, setIsPaused] = useState(true);

  const formatTime = useCallback((time: number) => {
    if (!Number.isFinite(time) || time < 0) {
      return '00:00';
    }
    const totalSeconds = Math.floor(time);
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  const togglePlayback = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (audioEl.paused) {
      audioEl.play();
      setIsPaused(false);
      if (typeof handleAudioPlay === 'function') {
        handleAudioPlay(true);
      }
    } else {
      audioEl.pause();
      setIsPaused(true);
      if (typeof handleAudioPlay === 'function') {
        handleAudioPlay(false);
      }
    }
  };

  const audioTimeUpdate = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    setCurrentTime(audioEl.currentTime);
    if (audioEl.ended) {
      setIsPaused(true);
      audioEl.currentTime = 0;
      setCurrentTime(0);
    }
  };

  useEffect(() => {
    const normalized = parseDuration(audioDuration);
    if (normalized && Number.isFinite(normalized)) {
      setDuration(normalized);
    }
  }, [audioDuration]);

  useEffect(() => {
    setCurrentTime(0);
    setIsPaused(true);
  }, [url]);

  const handleLoadedMetadata = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    const provided = parseDuration(audioDuration);
    const metadataDuration = audioEl.duration;
    const totalDuration = Math.max(provided ?? 0, Number.isFinite(metadataDuration) ? metadataDuration : 0);
    if (totalDuration > 0) {
      setDuration(totalDuration);
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <img
        className="w-8"
        src={!isPaused ? PauseButton : PlayButton}
        role="button"
        alt="play-button"
        onClick={togglePlayback}
      />
      {isTimeDisplay ? (
        <p className="text-dark-blue text-xs font-light">
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
      ) : null}
      <audio
        controls
        hidden
        preload="auto"
        ref={audioRef}
        src={url}
        onTimeUpdate={audioTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      ></audio>
    </div>
  );
};

export default AudioPlayer;
